import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import TranslationForm from '../components/TranslationForm';
import { getCategory, createCategory, updateCategory, getCategories } from '../../../services/blogService';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';

function CategoryEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    slug: '',
    parent_id: null,
    sort_order: 0,
    is_active: true,
    translations: [{ language_code: 'tr', name: '', description: '' }]
  });

  useEffect(() => {
    loadCategories();

    if (isEditing) {
      loadCategory();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await getCategories({ tree: 'false' });
      if (response.state && response.data) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadCategory = async () => {
    try {
      setLoading(true);
      const response = await getCategory(id);
      if (response.state && response.data) {
        const category = response.data;
        setFormData({
          slug: category.slug || '',
          parent_id: category.parent_id || null,
          sort_order: category.sort_order || 0,
          is_active: category.is_active !== false,
          translations: category.translations || [{ language_code: 'tr', name: '', description: '' }]
        });
      }
    } catch (error) {
      console.error('Error loading category:', error);
      toast.error('Kategori yüklenirken hata oluştu');
      navigate('/panel/blog?tab=categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (isEditing) {
        await updateCategory(id, formData);
        toast.success('Kategori başarıyla güncellendi');
      } else {
        await createCategory(formData);
        toast.success('Kategori başarıyla oluşturuldu');
      }

      navigate('/panel/blog?tab=categories');
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.message || 'Kategori kaydedilirken hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        <p className="text-zinc-400 text-sm">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('../?tab=categories')}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Geri
        </Button>
        <h1 className="text-2xl font-bold text-zinc-100">
          {isEditing ? 'Kategori Düzenle' : 'Yeni Kategori Oluştur'}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="md:col-span-2 space-y-1.5">
          <label className="text-xs text-gray-500 uppercase font-semibold">Slug</label>
          <Input
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="kategori-slug"
          />
        </div>

        <div className="md:col-span-2 space-y-1.5">
          <label className="text-xs text-gray-500 uppercase font-semibold">Üst Kategori</label>
          <Select
            value={formData.parent_id || ''}
            onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
          >
            <option value="">Yok</option>
            {categories.filter(c => !isEditing || c.id !== parseInt(id)).map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.translation?.name || cat.slug}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-gray-500 uppercase font-semibold">Sıralama</label>
          <Input
            type="number"
            value={formData.sort_order}
            onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
          />
        </div>

        <div className="flex items-center gap-2 pt-6">
          <input
            type="checkbox"
            id="is_active"
            className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-zinc-100 focus:ring-zinc-700"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />
          <label htmlFor="is_active" className="text-sm font-medium text-zinc-300">
            Aktif
          </label>
        </div>

        <div className="md:col-span-2">
          <TranslationForm
            translations={formData.translations}
            onChange={(translations) => setFormData({ ...formData, translations })}
            fields={['name', 'description']}
          />
        </div>

        <div className="md:col-span-2 flex justify-end gap-3 pt-6 border-t border-zinc-800">
          <Button variant="ghost" onClick={() => navigate('../?tab=categories')}>
            İptal
          </Button>
          <Button onClick={handleSave} className="px-8">
            Kaydet
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CategoryEditPage;

